import { SettingsPage } from "@risalabs_frontend_org/auth-wrapper";
import { Navigate, Outlet } from "react-router-dom";
import ErrorPage from "../components/errorPage/errorPage";
import ProtectedAdminRoute from "../components/protectedAdminRoute/protectedAdminRoute";
import { PharmaFormFieldsProvider } from "../context/pharmaFormFieldsContext";
import { OrganizationType } from "../enums/organizationTypes";
import { TableNames } from "../enums/tableNames";
import AsteraOncologySanity from "../pages/asteraOncologySanity/asteraOncologySanity";
import CallingWorklist from "../pages/callingWorklist/callingWorklist";
import CallingWorklistLayout from "../pages/callingWorklist/callingWorklistLayout";
import PendingWorklist from "../pages/callingWorklist/pendingWorklist";
import CmmOrder from "../pages/cmmOrder/cmmOrder";
import Configurations from "../pages/configurations/configurations";
import BiosimilarConfig from "../pages/configurations/pages/biosimilarConfig/biosimilarConfig";
import CallingConfiguration from "../pages/configurations/pages/callingConfiguration/callingConfiguration";
import DeliveryConfigurationLayout from "../pages/configurations/pages/deliveryConfiguration/deliveryConfigurationLayout";
import LetterUploadConfig from "../pages/configurations/pages/deliveryConfiguration/letterUpload/letterUploadConfig";
import DeliveryConfig from "../pages/configurations/pages/deliveryConfiguration/pages/deliveryConfig/deliveryConfig";
import NycbsSftpPushConfig from "../pages/configurations/pages/deliveryConfiguration/sftpPush/nycbsSftpPushConfig";
import TextNotesConfig from "../pages/configurations/pages/deliveryConfiguration/textNotes/textNotesConfig";
import TrackStatusConfig from "../pages/configurations/pages/deliveryConfiguration/trackStatus/trackStatusConfig";
import DocumentsToAttach from "../pages/configurations/pages/documentsConfiguration/documentsToAttach";
import FirstStpBucketing from "../pages/configurations/pages/firstStpBucketing/firstStpBucketing";
import FormConfig from "../pages/configurations/pages/formsConfiguration/formConfig";
import FormConfigurationLayout from "../pages/configurations/pages/formsConfiguration/formConfigurationLayout";
import SingleFormConfiguration from "../pages/configurations/pages/formsConfiguration/singleFormConfiguration/singleFormConfiguration";
import HealthFacilityConfiguration from "../pages/configurations/pages/healthFacilityConfiguration/healthFacilityConfiguration";
import MedicalNecessityConfiguration from "../pages/configurations/pages/medicalNecessityConfiguration/medicalNecessityConfiguration";
import MedicationList from "../pages/configurations/pages/medicationList/medicationList";
import MedicineNameConfiguration from "../pages/configurations/pages/medicineNameConfiguration/medicineNameConfiguration";
import PaFormConfiguration from "../pages/configurations/pages/paFormConfiguration/paFormConfiguration";
import ClinicalQuestionnaire from "../pages/configurations/pages/pharmaPaConfiguration/pages/clinicalQuestionnaire/clinicalQuestionnaire";
import DrugsConfiguration from "../pages/configurations/pages/pharmaPaConfiguration/pages/drugConfig/drugsConfig";
import PbmConfiguration from "../pages/configurations/pages/pharmaPaConfiguration/pages/pbmConfiguration/pbmConfiguration";
import ProviderList from "../pages/configurations/pages/pharmaPaConfiguration/pages/providerList/providerList";
import SendToPlanConfig from "../pages/configurations/pages/pharmaPaConfiguration/pages/sendToPlanConfig/sendToPlanConfig";
import PharmaPaConfigurationLayout from "../pages/configurations/pages/pharmaPaConfiguration/pharmaPaConfigurationLayout";
import PocConfiguration from "../pages/configurations/pages/pocConfiguration/pocConfiguration";
import PrescriptionParsingConfiguration from "../pages/configurations/pages/prescriptionParsingConfiguration/prescriptionParsingConfiguration";
import ProviderOptionsConfiguration from "../pages/configurations/pages/providerOptionsConfiguration/providerOptionsConfiguration";
import ConditionList from "../pages/configurations/pages/validationFunnel/pages/conditionList/conditionList";
import FirstStpLogic from "../pages/configurations/pages/validationFunnel/pages/firstStpLogic";
import PlayGround from "../pages/configurations/pages/validationFunnel/pages/playGround/playGround";
import ValidationFunnelValidationRules from "../pages/configurations/pages/validationFunnel/pages/validationRules";
import ValidationFunnel from "../pages/configurations/pages/validationFunnel/validationFunnel";
import CredentialsConfig from "../pages/credentialsConfigration/credentialsConfig";
import InsuranceDetailsLayout from "../pages/insuranceDetails/insuranceDetailsLayout";
import InternalProcessedOrderStatus from "../pages/internalProcessedOrderStatus/internalProcessedOrderStatus";
import PharmaOncologyAppealsTable from "../pages/pharmaOncologyAppeals/pharmaOncologyAppeals";
import PharmaOncologyAppealsLayout from "../pages/pharmaOncologyAppeals/pharmaOncologyAppealsLayout";
import NycbsExternal from "../pages/nycbsExternal/nycbsExternal";
import NycbsExternalLayout from "../pages/nycbsExternal/nycbsExternalLayout";
import PharmaOncologySubmissionTable from "../pages/pharmaOncologySubmission/pharmaOncologySubmission";
import PharmaOncologySubmissionLayout from "../pages/pharmaOncologySubmission/pharmaOncologySubmissionLayout";
import PharmaPaDiffDataLayout from "../pages/pharmaPaDiffData/pharmaPaDiffDataLayout";
import PharmaPaDiffViewer from "../pages/pharmaPaDiffViewer/pharmaPaDiffViewer";
import PharmaPaFormLayout from "../pages/pharmaPaForm/pharmaPaFormLayout";
import PharmaPaWorklistLayout from "../pages/pharmaPaWorklist/pharmaPaWorklistLayout";
import PharmaPrimaryCareLayout from "../pages/pharmaPrimaryCare/pharmaPrimaryCareLayout";
import PharmaPrimaryCareTable from "../pages/pharmaPrimaryCare/pharmaPrimaryCareTable";
import PharmaQuestionaireLayout from "../pages/pharmaQuestionaire/pharmaQuestionaireLayout";
import PharmaStpFile from "../pages/pharmaStpFile/pharmaStpFile";
import PharmaStpFileLayout from "../pages/pharmaStpFile/pharmaStpFileLayout";
import RequestOutcomeLayout from "../pages/requestOutcome/requestOutcomeLayout";
import Tasks from "../pages/tasks/tasks";
import TasksLayout from "../pages/tasks/tasksLayout";
import TestQuestionnaire from "../pages/testQuestionnaire/testQuestionnaire";
import { getOrgIdForFetchExternalWorklist } from "../utils/organizationHelper";

/**
 * Shared layout that provides PharmaFormFieldsContext across
 * insurance-details and pharma-pa-form routes so field values
 * persist when navigating between the two pages.
 */
const FormFieldsProviderLayout = () => (
  <PharmaFormFieldsProvider>
    <Outlet />
  </PharmaFormFieldsProvider>
);

const PharmaPaOrderTableByOrg = () => {
  const orgId = getOrgIdForFetchExternalWorklist();
  return orgId === OrganizationType.ASTERA ? (
    <AsteraOncologySanity />
  ) : (
    <CmmOrder />
  );
};

export const PharmaPaRouteConfig = [
  { index: true, element: <PharmaPaWorklistLayout /> },
  {
    path: "pharma-pa-worklists",
    element: <PharmaPaWorklistLayout />,
    children: [
      { index: true, element: <Navigate to="pharma-pa-orders" replace /> },
      { path: "pharma-pa-orders", element: <PharmaPaOrderTableByOrg /> },
      {
        element: <FormFieldsProviderLayout />,
        children: [
          {
            path: "pharma-pa-form/:id",
            element: <PharmaPaFormLayout />,
          },
          {
            path: "insurance-details/:id",
            element: <InsuranceDetailsLayout />,
          },
        ],
      },
      {
        path: "pharma-pa-questionaire/:id",
        element: <PharmaQuestionaireLayout />,
      },
      {
        path: "request-outcome/:id",
        element: <RequestOutcomeLayout />,
      },
    ],
  },
  {
    path: "pharma-pa-sftp-file",
    element: <PharmaStpFileLayout />,
    children: [
      { index: true, element: <Navigate to="sftp-file-orders" replace /> },
      { path: "sftp-file-orders", element: <PharmaStpFile /> },
      {
        element: <FormFieldsProviderLayout />,
        children: [
          {
            path: "pharma-pa-form/:id",
            element: <PharmaPaFormLayout />,
          },
          {
            path: "insurance-details/:id",
            element: <InsuranceDetailsLayout />,
          },
        ],
      },
      {
        path: "pharma-pa-questionaire/:id",
        element: <PharmaQuestionaireLayout />,
      },
    ],
  },
  {
    path: "nycbs-external",
    element: <NycbsExternalLayout />,
    children: [
      { index: true, element: <Navigate to="sftp-file-orders" replace /> },
      {
        path: "sftp-file-orders",
        element: <NycbsExternal orgId={OrganizationType.NYCBS_PHARMA} />,
      },
      {
        path: "stat-orders",
        element: <NycbsExternal orgId={OrganizationType.NYCBS_STATS} />,
      },
      {
        element: <FormFieldsProviderLayout />,
        children: [
          {
            path: "pharma-pa-form/:id",
            element: <PharmaPaFormLayout />,
          },
          {
            path: "insurance-details/:id",
            element: <InsuranceDetailsLayout />,
          },
        ],
      },
      {
        path: "pharma-pa-questionaire/:id",
        element: <PharmaQuestionaireLayout />,
      },
    ],
  },
  {
    path: "pharma-pa-calling-worklist",
    element: <CallingWorklistLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="calling-worklist-orders" replace />,
      },
      { path: "calling-worklist-orders", element: <CallingWorklist /> },
      {
        path: "pharma-pa-form/:id",
        element: (
          <PharmaFormFieldsProvider>
            <PharmaPaFormLayout />
          </PharmaFormFieldsProvider>
        ),
      },
      {
        path: "pharma-pa-questionaire/:id",
        element: <PharmaQuestionaireLayout />,
      },
      {
        path: "insurance-details/:id",
        element: (
          <PharmaFormFieldsProvider>
            <InsuranceDetailsLayout />
          </PharmaFormFieldsProvider>
        ),
      },
    ],
  },
  {
    path: "pharma-pa-pending-worklist",
    element: (
      <CallingWorklistLayout tableName={TableNames.PENDING_WORKLIST_ORDERS} />
    ),
    children: [
      {
        index: true,
        element: <Navigate to="pending-worklist-orders" replace />,
      },
      { path: "pending-worklist-orders", element: <PendingWorklist /> },
    ],
  },
  {
    path: "pharma-oncology-submissions",
    element: <PharmaOncologySubmissionLayout />,
    children: [
      { index: true, element: <Navigate to="submission-orders" replace /> },
      { path: "submission-orders", element: <PharmaOncologySubmissionTable /> },
    ],
  },
  {
    path: "pharma-oncology-appeals",
    element: <PharmaOncologyAppealsLayout />,
    children: [
      { index: true, element: <Navigate to="case-appeals" replace /> },
      { path: "case-appeals", element: <PharmaOncologyAppealsTable /> },
    ],
  },
  {
    path: "pharma-pa-primary-care",
    element: <PharmaPrimaryCareLayout />,
    children: [
      { index: true, element: <Navigate to="primary-care-orders" replace /> },
      { path: "primary-care-orders", element: <PharmaPrimaryCareTable /> },
      {
        element: <FormFieldsProviderLayout />,
        children: [
          {
            path: "insurance-details/:id",
            element: <InsuranceDetailsLayout />,
          },
          {
            path: "pharma-pa-form/:id",
            element: <PharmaPaFormLayout />,
          },
        ],
      },
    ],
  },

  {
    path: "tasks",
    element: <TasksLayout />,
    children: [
      { index: true, element: <Navigate to="task-list" replace /> },
      { path: "task-list", element: <Tasks /> },
    ],
  },
  {
    path: "pharma-pa-diff-data",
    element: <PharmaPaWorklistLayout />,
    children: [
      { index: true, element: <Navigate to="diff-data-table" replace /> },
      { path: "diff-data-table", element: <PharmaPaDiffDataLayout /> },
      {
        path: "diff-data-viewer/:id",
        element: <PharmaPaDiffViewer />,
      },
    ],
  },
  {
    path: "pharma-pa-status-internal",
    element: <InternalProcessedOrderStatus />,
  },
  {
    path: "configurations",
    element: (
      <ProtectedAdminRoute>
        <Configurations />
      </ProtectedAdminRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="documents-configuration" replace />,
      },
      { path: "documents-configuration", element: <DocumentsToAttach /> },
      {
        path: "medical-necessity-configuration",
        element: <MedicalNecessityConfiguration />,
      },
      {
        path: "health-facility",
        element: <HealthFacilityConfiguration />,
      },
      {
        path: "form-configuration",
        element: <FormConfigurationLayout />,
        children: [
          { index: true, element: <Navigate to="form-config" replace /> },
          { path: "form-config", element: <FormConfig /> },
          {
            path: "form-config/:id",
            element: <SingleFormConfiguration />,
          },
        ],
      },
      {
        path: "pa-form-configuration",
        element: <PaFormConfiguration />,
      },
      {
        path: "medicine-name-configuration",
        element: <MedicineNameConfiguration />,
      },
      {
        path: "prescription-parsing-configuration",
        element: <PrescriptionParsingConfiguration />,
      },
      {
        path: "pharma-pa-configuration",
        element: <PharmaPaConfigurationLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="send-to-plan-config" replace />,
          },
          { path: "send-to-plan-config", element: <SendToPlanConfig /> },
          { path: "pbm-configurations", element: <PbmConfiguration /> },
          { path: "drugs-configuration", element: <DrugsConfiguration /> },
          { path: "clinical-questionaire", element: <ClinicalQuestionnaire /> },
        ],
      },
      {
        path: "delivery-configuration",
        element: <DeliveryConfigurationLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="track-status" replace />,
          },
          { path: "track-status", element: <TrackStatusConfig /> },
          { path: "text-notes", element: <TextNotesConfig /> },
          { path: "letter-upload", element: <LetterUploadConfig /> },
          { path: "sftp-push", element: <NycbsSftpPushConfig /> },
          { path: "delivery-settings", element: <DeliveryConfig /> },
        ],
      },

      {
        path: "credentials-configuration",
        element: <CredentialsConfig />,
      },
      {
        path: "validation-funnel",
        element: <ValidationFunnel />,
        children: [
          { index: true, element: <Navigate to="first-stp-logic" replace /> },
          {
            path: "first-stp-logic",
            element: <FirstStpLogic key="first" variant="first" />,
          },
          {
            path: "second-stp-logic",
            element: <FirstStpLogic key="second" variant="second" />,
          },
          {
            path: "agentic-logic",
            element: <FirstStpLogic key="agentic" variant="agentic" />,
          },
          { path: "condition-list", element: <ConditionList /> },
          { path: "playground", element: <PlayGround /> },
          {
            path: "validation-rules",
            element: <ValidationFunnelValidationRules />,
          },
        ],
      },
      {
        path: "first-stp-bucketing",
        element: <FirstStpBucketing />,
      },
      // { path: "client-configuration", element: <ClientConfiguration /> },
      { path: "provider-list", element: <ProviderList /> },
      { path: "medication-list", element: <MedicationList /> },
      { path: "biosimilar-config", element: <BiosimilarConfig /> },
      {
        path: "calling-configuration",
        element: <CallingConfiguration />,
      },
      {
        path: "poc-configuration",
        element: <PocConfiguration />,
      },
      {
        path: "provider-options-configuration",
        element: <ProviderOptionsConfiguration />,
      },

      // { path: "payer-config", element: <PayerConfiguration /> },
      // { path: "nar-agent-grid", element: <NarAgentGridLayout /> },
      // {
      //   path: "health-facility",
      //   element: <HealthFacilityConfiguration />,
      // },
    ],
  },
  {
    path: "settings/*",
    element: (
      <SettingsPage
        enabledPages={[
          "profile",
          "security",
          "members",
          "support",
          "superadmin",
        ]}
        roleOptions={[
          { value: "admin_pharma", label: "Pharmacy Admin" },
          { value: "standard_pharma", label: "Standard Pharmacy" },
        ]}
      />
    ),
  },

  {
    path: "test-questionnaire",
    element: <TestQuestionnaire />,
  },

  {
    path: "*",
    element: <ErrorPage />,
  },
];
